import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/Button";

type RootErrorBoundaryProps = {
  children: ReactNode;
};

type RootErrorBoundaryState = {
  hasError: boolean;
  errorMessage: string | null;
};

export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = {
    hasError: false,
    errorMessage: null,
  };

  static getDerivedStateFromError(error: unknown): RootErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : "Unexpected render error",
    };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.error("[RootErrorBoundary] Render failed.", error, errorInfo);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h1 className="text-xl font-semibold">Application failed to render</h1>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
            A runtime error stopped the app before it could render. Reload the page after
            fixing the underlying issue.
          </p>
          {import.meta.env.DEV && this.state.errorMessage ? (
            <pre className="mt-4 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs text-slate-700 dark:bg-slate-950 dark:text-slate-300">
              {this.state.errorMessage}
            </pre>
          ) : null}
          <Button
            type="button"
            variant="primary"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-lg"
          >
            Reload page
          </Button>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
